import React, { useCallback, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { lookInside } from '../data/content';
import { pageCaptions } from '../data/images';
import { ImageSlot } from './ImageSlot';
import { Lightbox } from './Lightbox';
import { SectionHeading } from './SectionHeading';
type LookInsideSectionProps = {
  pageImages: string[];
};

/** Captions live next to the images (data/images.ts) so they always match the pages shown. */
const captions = pageCaptions.length ? pageCaptions : lookInside.captions;

/** "See what's inside": real 9:16 pages of the book. Wide cards on mobile (one page at a time), tap to open full size. */
export function LookInsideSection({
  pageImages
}: LookInsideSectionProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);

  // Hidden until the real pages of the book are loaded in data/images.ts
  if (pageImages.length === 0) return null;
  return <section id="look-inside" aria-labelledby="look-title" className="scroll-mt-4 bg-ink py-12 md:py-20" data-id="element-127" data-mp="3b46x2xvn0gkb">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-128" data-mp="3g39tjtxjxego">
        <SectionHeading id="look-title" tone="dark" eyebrow={lookInside.eyebrow} title={lookInside.title} sub={lookInside.sub} data-id="element-129" data-mp="3g3rtb0jfaatc" />
        <p className="mt-3 text-center text-[13px] text-white/75" data-id="element-130" data-mp="3l0i9roz538fa">{lookInside.hint}</p>
      </div>

      <ul className="no-scrollbar mx-auto mt-8 flex max-w-page snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-5 px-5 pb-2 md:mt-10 md:gap-4 md:scroll-px-8 md:px-8" aria-label="Sample pages" data-id="element-131" data-mp="3puwdcenyglv1">
        {pageImages.map((url, i) => <li key={captions[i] ?? i} className="w-[80%] shrink-0 snap-center sm:w-[46%] md:w-[31%] lg:w-[23%]" data-id="element-132" data-mp="7nzt0q9b8hb0" data-mpi={i}>
            <button type="button" onClick={() => setActive(i)} className="group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sun" aria-label={`Open full size: ${captions[i]}`} data-id="element-133" data-mp="clruobzrbhse">
              <div className="relative aspect-[9/16] overflow-hidden rounded-xl bg-[#1B2233] ring-1 ring-white/10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.8)]" data-id="element-134" data-mp="26f1l0lkql385">
                <ImageSlot url={url} alt={captions[i]} slotName={`pageImages[${i}]`} fit="contain" className="absolute inset-0" data-id="element-135" data-mp="2gczatdi8v82v" />
                <span className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-sun text-ink shadow-md transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-136" data-mp="2beo0l1o1evk4">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="2z1w5hihk02lm" />
                </span>
              </div>
              <p className="mt-3 text-[15px] font-medium text-white" data-id="element-138" data-mp="2u32sdr66idlz">
                <span className="mr-2 font-display text-[17px] font-extrabold text-sun" data-id="element-139" data-mp="2u4vx30x8enrc">{String(i + 1).padStart(2, '0')}</span>
                {captions[i]}
              </p>
            </button>
          </li>)}
      </ul>
      <p className="mx-auto mt-4 max-w-page px-5 text-center text-[13px] text-white/70 md:px-8" data-id="element-140" data-mp="3mpiyh8omb4wc">{lookInside.note}</p>

      <Lightbox images={pageImages} captions={captions} index={active} onClose={close} onChange={setActive} data-id="element-141" data-mp="3rom5gyo5rl6y" />
    </section>;
}